import { useEffect, useMemo, useState } from "react"

import { BackgroundImageTag, ContentTag, Obj, load } from "scrivito"
import { BuildingReferenceObj } from "scrivito/Objs/BuildingReference/BuildingReferenceObjClass"
import { InstallationReferenceObj } from "scrivito/Objs/InstallationReference/InstallationReferenceObjClass"
import {
  ProductAttributes,
  ProductObj
} from "scrivito/Objs/Product/ProductObjClass"
import {
  ProductBaseVariantAttributes,
  ProductBaseVariantObj
} from "scrivito/Objs/ProductBaseVariant/ProductBaseVariantObjClass"
import { useScrivitoChildren } from "scrivito/hooks/useScrivitoChildren"
import { useScrivitoPage } from "scrivito/hooks/useScrivitoPage"
import { ScrivitoPageBreadcrumb } from "scrivito/scrivito-breadcrumb/ScrivitoPageBreadcrumb"
import { ScrivitoImage } from "scrivito/scrivito-image/ScrivitoImage"
import { SelectOption } from "shared/types/Select"
import { CarouselItem } from "shared/ui/carousel/Carousel"

import { useInstallationReferenceObjFilter } from "entities/building-references/model/useInstallationReferenceObjFilter"
import { InstallationReferenceFilterState } from "entities/building-references/types/InstallationReferenceFilterState"

import { BuildingReferencePageProps } from "../BuildingReferencePage"
import {
  BuildingReferencePageViewModel,
  InstallationReferenceItem
} from "../BuildingReferencePageView"
import { useBuildingReferencePageGeneralInfoViewModel } from "../components/hooks/useBuildingReferencePageGeneralInfoViewModel"
import { InstallationReferenceTabContent } from "../components/installation-reference-tab-content/InstallationReferenceTabContent"
import { InstallationReferenceTags } from "../components/installation-reference-tags/InstallationReferenceTags"
import { useBuildingReferenceState } from "./useBuildingReferenceState"
import { useScrollToInstallationReference } from "./useScrollToInstallationReference"

export function useBuildingReferencePageViewModel({
  page
}: BuildingReferencePageProps): BuildingReferencePageViewModel {
  useScrivitoPage()

  const [productFilterOptions, setProductFilterOptions] = useState<
    SelectOption<string>[]
  >([])

  const { activeProductFilters, setActiveProductFilters } =
    useBuildingReferenceState(page.id())

  const { children: installationRefObjs } = useScrivitoChildren<
    BuildingReferenceObj,
    InstallationReferenceObj
  >({
    page
  })

  const imageWidgets = page?.get("imageWidgets")

  const galleryImages: CarouselItem[] =
    imageWidgets?.map(widget => {
      return {
        title: widget?.id(),
        key: widget?.id(),
        content: (
          <BackgroundImageTag className="d-flex flex-grow-1">
            <ScrivitoImage
              content={widget}
              attribute="image"
              className="building-reference-image-carousel__image"
              alt={widget?.get("alternativeText") as string}
            />
          </BackgroundImageTag>
        )
      }
    }) || []

  const filters: InstallationReferenceFilterState = useMemo(
    () => ({ products: activeProductFilters }),
    [activeProductFilters]
  )

  const filteredInstallationReferenceObjs = useInstallationReferenceObjFilter({
    installationReferenceObjs: installationRefObjs,
    filters
  })

  const installationReferences: InstallationReferenceItem[] = useMemo(
    () =>
      filteredInstallationReferenceObjs?.map(
        (obj: InstallationReferenceObj) =>
          ({
            title: obj.get("title"),
            titleSlot: (
              <InstallationReferenceTags installationReferenceObj={obj} />
            ),
            content: (
              <InstallationReferenceTabContent installationReferenceObj={obj} />
            ),
            key: obj.get("referenceID")
          } as InstallationReferenceItem)
      ) || [],
    [filteredInstallationReferenceObjs, activeProductFilters]
  )

  useEffect(() => {
    Promise.all(
      installationRefObjs.map((obj: InstallationReferenceObj) =>
        load(() => obj.get("product"))
      )
    ).then(products =>
      setProductFilterOptions(
        Array.from(
          new Set(
            (
              products.filter(Boolean) as (ProductObj | ProductBaseVariantObj)[]
            ).map(
              (
                product: Obj<ProductAttributes | ProductBaseVariantAttributes>
              ) => product.get("variantKey")
            )
          )
        ).map(
          (variantKey: string) =>
            ({
              value: variantKey,
              label: variantKey
            } as SelectOption<string>)
        )
      )
    )
  }, [installationRefObjs])

  useScrollToInstallationReference({
    items: installationReferences
  })

  const generalInfoViewModel = useBuildingReferencePageGeneralInfoViewModel({
    buildingReferenceObj: page
  })

  return {
    breadcrumbSlot: <ScrivitoPageBreadcrumb page={page} />,

    titleSlot: <ContentTag content={page} attribute="title" tag="span" />,
    images: galleryImages,
    generalInfoViewModel,
    installationReferences,

    productFilterOptions,
    activeProductFilters,
    onActiveProductFiltersChange: setActiveProductFilters
  }
}
